/* eslint-disable react/jsx-indent */
import { State } from 'redux/initialState';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';

import menuTypes from 'constants/menuTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetOperations } from 'modules/transactions/hooks/useGetOperations';
import { useTransactionsList } from 'modules/transactions/pages/TransactionsList/hooks/useTransactionsList';
import { useGetOperationsMenu } from 'modules/transactions/hooks/useGetOperationsMenu';
import { useGetTransactionsSectionPermissions } from 'modules/transactions/hooks/useGetTransactionsSectionPermissions';

import Page from 'modules/_shared/components/Pages/Page';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import transformData from 'modules/_shared/components/Tables/helpers';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import OperationTypesSelector from 'modules/transactions/components/OperationTypesSelector';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 10;

const TransactionsList = () => {
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const dispatch = useDispatch();
  const { societyId, partnerId } = useParams();

  const user = useSelector((state: State) => state?.user);
  const operations = useSelector((state: State) => state?.operations);
  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety
  );

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const [page, setPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);

  const { data: operationsMenu } = useGetOperationsMenu();
  const { isAdmin, isReadOnly } = useGetTransactionsSectionPermissions().data;

  const { columns, tableData, filterBy, setFilterBy } = useTransactionsList({
    societyId: societyId || '',
    partnerId: partnerId || '',
    isReadOnly,
    operations,
  });

  const { isLoading } = useGetOperations({
    societyId,
    partnerId,
    page,
    items: rowsPerPage,
    sortBy: 'date',
    sortOrder: 'desc',
    filterBy: filterBy === 'ALL' ? '' : filterBy,
  });

  const getOperationsDescription = () =>
    tableData?.length > 0
      ? t('DetailOfLatestOperations', { societyName: actualSociety?.name })
      : t('YouHaveNoTransactions');

  const getFilteredData = useMemo(() => {
    if (filterBy !== 'ALL') {
      return tableData?.filter((row) => row?.operationType === filterBy);
    }
    return tableData;
  }, [filterBy, tableData]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsCount: number) => {
    setRowsPerPage(newRowsCount);
  };

  return (
    <Page>
      <PageListHeader
        subTitle={t('Transactions')}
        description={getOperationsDescription()}
        actions={
          isAdmin && [
            <OperationTypesSelector
              societyId={societyId}
              filterBy={filterBy}
              onChangeFilter={setFilterBy}
            />,
            <OperationMenuDots
              menuOptions={operationsMenu}
              handleModal={(Modal: any) =>
                dispatch(
                  setModal(
                    <Modal user={user} societyId={actualSociety['_id']} />
                  )
                )
              }
              params={{ society: actualSociety, user, mixpanel }}
              disabled={isReadOnly}
            />,
          ]
        }
      />

      {tableData?.length > 0 && (
        <>
          <CustomDataTable
            data={transformData({
              data: getFilteredData,
              columns,
            })}
            columns={columns}
            searchBy={t('Operation')}
            pagination
            className="nk-tb-list"
            // expandableRows
            actions
            showDense
            showDownload={false} // TODO: [AXA] Enable download
            toggleNationality={false}
            fileName={`${t('Transactions')} ${actualSociety.name}`}
            hasCustomPagination
            customPagination={{
              className: 'bg-white',
              totalItems: actualSociety?.operationsCount?.total || 0,
              itemPerPage: rowsPerPage,
              currentPage: page,
              customItemPerPage: rowsPerPage,
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
              setRowsPerPage,
            }}
            onFilterChange={undefined}
          />
        </>
      )}

      {isLoading && <CustomLoading />}
    </Page>
  );
};

export default TransactionsList;
